import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getCurrentUser, logout, isMasterContext, selectAgency } from '../services/authService';
import {
  Menu, Shield, X, LayoutGrid, FileText, Users, BookCopy, ChevronDown, Tag, Settings,
  LogOut, Check, Network, ClipboardCheck, FileCheck, Send, FileBarChart, GitBranchPlus, Contact,
  ListChecks, Building2, ArrowLeftRight, Workflow, BookCheck, FolderOpen, PanelLeftClose,
  PanelLeftOpen, Library, Landmark, SlidersHorizontal, ChartColumn,
} from 'lucide-react';
import './Sidebar.css';
import { roleLabel } from '../services/roleLabels';

const COLLAPSED_GROUPS_KEY = 'veriform.nav.collapsedGroups';
const SLIM_KEY = 'veriform.nav.slim';

// Per-viewer conveniences only (which groups are folded, whether the rail is slim). A
// private window or blocked storage throws, and the nav must still render — so every
// read falls back to the default and every write is best-effort.
const readStored = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? fallback : JSON.parse(raw);
  } catch {
    return fallback;
  }
};
const writeStored = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage unavailable — the choice lasts for this page view only.
  }
};

// Three widths, three shapes of the same navigation (Sidebar.css mirrors each query):
//   phone   (up to 768px)   — a drawer behind the menu button in the top bar;
//   tablet  (769 to 1100px) — the icon rail, always; "Expand menu" lays the full menu
//                             over the page and nothing is remembered;
//   desktop (1101px and up) — the full rail, or the icon rail if this viewer chose it.
// The slim rail exists only above the phone width; below it the drawer shows full headings
// whatever the remembered choice, so code that reads `isSlim` must ask `isDesktop` too.
const DESKTOP_QUERY = '(min-width: 769px)';
const TABLET_QUERY = '(min-width: 769px) and (max-width: 1100px)';
const mediaQuery = (query) => (window.matchMedia ? window.matchMedia(query) : null);
const desktopQuery = () => mediaQuery(DESKTOP_QUERY);
const tabletQuery = () => mediaQuery(TABLET_QUERY);

const exact = (to) => (pathname) => pathname === to;
const prefix = (to) => (pathname) => pathname.startsWith(to);

/**
 * Every entry the signed-in person may reach, arranged as a few ungrouped essentials
 * followed by named groups. Who sees what is decided here and only here; the render
 * below never re-checks a role. A group whose entries are all hidden is dropped, so no
 * one ever sees an empty heading.
 */
export function buildNavigation(user, inMasterContext) {
  const inTenant = !inMasterContext;
  const role = user?.role;
  const isInitialAdmin = role === 'INITIAL_ADMIN';
  const isAgencyAdmin = isInitialAdmin || role === 'AGENCY_ADMIN';
  const isGroupAdmin = isAgencyAdmin || role === 'GROUP_ADMIN';

  // Dual-mode as of P8-09: in a tenant this is the agency roster, in master
  // context it is the platform-admin directory backed by the @MasterAllowed
  // DirectoryUserController. Without the master arm the route would be reachable
  // only by typing the URL.
  const canManageUsers = (inTenant && isGroupAdmin) || (inMasterContext && isInitialAdmin);
  // Gates the TOP-LEVEL in-tenant "Ranks" entry only, so it stays inTenant-only
  // even though RankStructureController is master-allowed as of #150. Master context
  // reaches the same route through the Content Library group ("Rank Structures"),
  // mirroring how the in-tenant "Forms" link and the master "Base Forms" entry split
  // one route across two contexts — one entry point per context, never two links to
  // /ranks at once.
  const canManageRanks = inTenant && isAgencyAdmin;
  // Policy-acknowledgement admin features (Distribute Policy, Ack. Report) are
  // open to GROUP_ADMIN per PRD-05 §9 and the backend @PreAuthorize on
  // PolicyAcknowledgementController.distributePolicy / getReport.
  const canManagePolicies = inTenant && isGroupAdmin;
  // DOC-22 (#470): every workflow template endpoint is hasRole('GROUP_ADMIN'), and they are
  // tenant-only (not @MasterAllowed), so the same predicate. Kept as its own name because the two
  // gates answer different questions and need not move together.
  const canManageWorkflowTemplates = inTenant && isGroupAdmin;
  // DOC-27 (#475): every test library endpoint is hasRole('GROUP_ADMIN') and tenant-only, and every
  // one of those readers may see the answer key (#550, ruled A on 2026-10-02). Its own name, as above.
  const canManageDocumentTests = inTenant && isGroupAdmin;

  const top = [
    inTenant && { to: '/', label: 'Dashboard', icon: LayoutGrid, match: exact('/') },
    // The agency admin's second page: what needs an administrator, over the agency's record.
    // Their '/' is the ordinary Dashboard — their own work — so administering the agency gets
    // its own entry rather than a link buried in the page. Tenant-only, like its data.
    inTenant && isAgencyAdmin && {
      to: '/administration', label: 'Administration', icon: Landmark, match: exact('/administration'),
    },
    // The agency's record and the settings its administrators control. Same audience as
    // Administration; anyone below agency administrator has nothing to change there.
    inTenant && isAgencyAdmin && {
      to: '/agency-settings', label: 'Agency Settings', icon: SlidersHorizontal, match: exact('/agency-settings'),
    },
    // The agency-wide figures: a platform administrator inside an agency, and nobody else for
    // now (owner ruling, 2026-10-28).
    inTenant && isInitialAdmin && {
      to: '/agency-reports', label: 'Agency Reports', icon: ChartColumn, match: exact('/agency-reports'),
    },
    // The registry endpoints are @MasterAllowed, so an INITIAL_ADMIN reaches them from
    // inside a tenant too, with no deselect first.
    isInitialAdmin && { to: '/agencies', label: 'Agencies', icon: Building2, match: exact('/agencies') },
    inMasterContext && canManageUsers && { to: '/users', label: 'Users', icon: Users, match: exact('/users') },
  ];

  const groups = [
    {
      id: 'forms',
      label: 'Forms & Reports',
      icon: ClipboardCheck,
      items: [
        // The tenant's own forms library. Ungated by role on purpose: this is
        // the ONLY route into the submit flow — BaseFormManagement renders a
        // PUBLISHED-only "Fill Out" button for a REGULAR_USER and is the sole
        // caller of /forms/:id/submit — so gating it by role would leave
        // ordinary users no way to fill out a form at all. Authoring inside
        // the screen is already role-gated. Agency-local authoring is
        // first-class for an AGENCY_ADMIN (WD-4).
        inTenant && { to: '/base-forms', label: 'Forms', icon: FileText, match: prefix('/base-forms') },
        inTenant && { to: '/my-submissions', label: 'My Submissions', icon: ListChecks, match: exact('/my-submissions') },
        inTenant && { to: '/form-submissions', label: 'Approvals', icon: ClipboardCheck, match: prefix('/form-submissions') },
      ],
    },
    {
      id: 'documents',
      label: 'Documents & Policies',
      icon: FolderOpen,
      items: [
        inTenant && { to: '/documents', label: 'Documents', icon: FileText, match: prefix('/documents') },
        inTenant && { to: '/acknowledgements', label: 'Acknowledgements', icon: FileCheck, match: exact('/acknowledgements') },
        canManagePolicies && { to: '/policy-distribution', label: 'Distribute Policy', icon: Send, match: exact('/policy-distribution') },
        canManagePolicies && { to: '/acknowledgement-report', label: 'Ack. Report', icon: FileBarChart, match: exact('/acknowledgement-report') },
        canManageWorkflowTemplates && { to: '/workflow-templates', label: 'Workflow Templates', icon: Workflow, match: prefix('/workflow-templates') },
        canManageDocumentTests && { to: '/document-tests', label: 'Test Library', icon: BookCheck, match: prefix('/document-tests') },
      ],
    },
    {
      id: 'people',
      label: 'People & Structure',
      icon: Contact,
      items: [
        // D15: the organization tree is whole-agency and read-only for EVERY role in
        // a tenant, so this entry is gated on `inTenant` alone. It stays absent in
        // master context — the endpoints are tenant-only, so a master-claim click
        // would 403 and eject the admin to the agency picker.
        inTenant && { to: '/organization', label: 'Organization', icon: GitBranchPlus, match: exact('/organization') },
        inTenant && { to: '/directory', label: 'Directory', icon: Contact, match: exact('/directory') },
        inTenant && canManageUsers && { to: '/users', label: 'Users', icon: Users, match: exact('/users') },
        canManageRanks && { to: '/ranks', label: 'Ranks', icon: Network, match: exact('/ranks') },
      ],
    },
    {
      // The MASTER content library — the catalog authored once and copied into every
      // tenant. Scoped to master context so it cannot sit alongside the in-tenant
      // "Forms" link pointing at the same route: in-tenant an admin edits that
      // agency's library, from master they edit the catalog. One entry point per context.
      id: 'content-library',
      label: 'Content Library',
      icon: BookCopy,
      items: isInitialAdmin && inMasterContext ? [
        // BaseFormController and RankStructureController carry a type-level
        // @MasterAllowed (#150), so these two serve the master catalog instead of
        // 403ing. "Rank Structures" rather than the in-tenant "Ranks" label: from
        // master these are the catalog originals copied into every tenant.
        { to: '/base-forms', label: 'Base Forms', icon: FileText, match: prefix('/base-forms') },
        { to: '/ranks', label: 'Rank Structures', icon: Network, match: exact('/ranks') },
        // ORG-29 (#324): the starter organization structures an INITIAL_ADMIN authors
        // and publishes to every agency. Master only — a tenant reaches its own copies
        // from the Organization screen.
        { to: '/org-structure-templates', label: 'Organization Structures', icon: Library, match: exact('/org-structure-templates') },
        { to: '/tags', label: 'Agency Tags', icon: Tag, match: exact('/tags') },
      ] : [],
    },
  ];

  return {
    top: top.filter(Boolean),
    groups: groups
      .map((group) => ({ ...group, items: group.items.filter(Boolean) }))
      .filter((group) => group.items.length > 0),
  };
}

function Sidebar() {
  const user = getCurrentUser();
  const location = useLocation();
  // Three contexts, not one. A master-claim admin has no tenant bound, so every
  // tenant-scoped link would 403 — showing them is offering broken navigation.
  const inMasterContext = isMasterContext();
  const inTenant = !inMasterContext;
  const isInitialAdmin = user && user.role === 'INITIAL_ADMIN';
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    const stored = readStored(COLLAPSED_GROUPS_KEY, []);
    return Array.isArray(stored) ? stored : [];
  });
  const [isSlim, setIsSlim] = useState(() => readStored(SLIM_KEY, false) === true);
  const [isDesktop, setIsDesktop] = useState(() => desktopQuery()?.matches ?? true);
  const [isTablet, setIsTablet] = useState(() => tabletQuery()?.matches ?? false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  // The full menu laid over the page: the phone's drawer and the tablet's expanded rail.
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  // A tablet's rail is slim unless the full menu is open over the page; a desktop's is
  // whatever this viewer last chose.
  const railIsSlim = isTablet ? !isMobileOpen : isSlim;
  const slimShown = railIsSlim && isDesktop;
  const menuButtonRef = useRef(null);
  const closeButtonRef = useRef(null);
  const collapseToggleRef = useRef(null);
  const [switchError, setSwitchError] = useState('');
  const [slimTip, setSlimTip] = useState(null);

  const { top, groups } = buildNavigation(user, inMasterContext);

  // Arriving on a page always shows where it sits: a folded group that holds the
  // current page unfolds rather than leaving the active entry out of sight.
  const activeGroupId = groups.find((g) => g.items.some((item) => item.match(location.pathname)))?.id;
  useEffect(() => {
    if (activeGroupId) {
      setCollapsedGroups((current) => (
        current.includes(activeGroupId) ? current.filter((id) => id !== activeGroupId) : current
      ));
    }
  }, [activeGroupId]);

  useEffect(() => {
    const desktop = desktopQuery();
    const tablet = tabletQuery();
    if (!desktop || !tablet) return undefined;
    const update = () => {
      setIsDesktop(desktop.matches);
      setIsTablet(tablet.matches);
      // Crossing a width boundary (a rotated tablet, a resized window) changes what
      // "open" means, so the menu starts closed on the other side.
      setIsMobileOpen(false);
    };
    setIsDesktop(desktop.matches);
    setIsTablet(tablet.matches);
    desktop.addEventListener('change', update);
    tablet.addEventListener('change', update);
    return () => {
      desktop.removeEventListener('change', update);
      tablet.removeEventListener('change', update);
    };
  }, []);

  // The open menu covers the page, so it behaves as a dialog would: Escape closes it, and
  // focus goes into it on opening and back to the control that opened it on closing.
  const menuOverlays = isMobileOpen && (isTablet || !isDesktop);
  useEffect(() => {
    if (!menuOverlays) return undefined;
    const opener = isTablet ? collapseToggleRef.current : menuButtonRef.current;
    closeButtonRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setIsMobileOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      opener?.focus();
    };
  }, [menuOverlays, isTablet]);

  useEffect(() => {
    writeStored(COLLAPSED_GROUPS_KEY, collapsedGroups);
  }, [collapsedGroups]);

  const toggleGroup = (id) => {
    setCollapsedGroups((current) => (
      current.includes(id) ? current.filter((g) => g !== id) : [...current, id]
    ));
  };

  const toggleSlim = () => {
    // On a tablet the switch opens and closes the full menu over the page; the remembered
    // choice belongs to the desktop rail and is left alone.
    if (isTablet) {
      setIsMobileOpen((current) => !current);
      return;
    }
    setIsSlim((current) => {
      writeStored(SLIM_KEY, !current);
      return !current;
    });
  };

  const handleLogout = () => {
    logout();
  };

  // selectAgency can reject (network, or the agency changed under us). On success
  // it hard-reloads, so there is no success path to handle here — but an
  // unhandled rejection would leave the admin staring at an unchanged sidebar
  // with only a console error, which reads as a dead button.
  const handleSwitchAgency = async () => {
    setSwitchError('');
    try {
      await selectAgency(null);
    } catch {
      setSwitchError('Could not switch agencies. Please try again.');
    }
  };

  const getUserInitials = () => {
    if (!user || !user.username) return 'U';
    const parts = user.username.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return user.username.substring(0, 2).toUpperCase();
  };

  const closeMobileSidebar = () => setIsMobileOpen(false);

  // A slim rail shows icons alone, so each one's name is drawn beside it on hover and on
  // keyboard focus. Read from the control's own data-label, positioned from its box.
  const showSlimTip = (event) => {
    const target = slimShown && event.target.closest ? event.target.closest('[data-label]') : null;
    if (!target) {
      setSlimTip(null);
      return;
    }
    const box = target.getBoundingClientRect();
    setSlimTip({ label: target.dataset.label, top: box.top + box.height / 2 });
  };
  const hideSlimTip = () => setSlimTip(null);

  const renderItem = (item) => {
    const Icon = item.icon;
    const active = item.match(location.pathname);
    return (
      <li key={item.to}>
        <Link
          to={item.to}
          className={active ? 'nav-item active' : 'nav-item'}
          aria-current={active ? 'page' : undefined}
          data-label={item.label}
          onClick={closeMobileSidebar}
        >
          <Icon size={18} aria-hidden="true" />
          <span className="nav-item-label">{item.label}</span>
        </Link>
      </li>
    );
  };

  const renderGroup = (group) => {
    const Icon = group.icon;
    const listId = `nav-group-${group.id}`;
    const headingId = `${listId}-heading`;
    // A slim rail has no room for headings, so every group shows its entries and the
    // groups are told apart by a rule between them.
    const open = slimShown || !collapsedGroups.includes(group.id);
    const holdsActive = group.id === activeGroupId;

    return (
      <li key={group.id} className={`nav-group${holdsActive ? ' nav-group-current' : ''}`}>
        <button
          type="button"
          className="nav-group-toggle"
          id={headingId}
          onClick={() => toggleGroup(group.id)}
          aria-expanded={open}
          aria-controls={listId}
        >
          <Icon size={18} aria-hidden="true" className="nav-group-icon" />
          <span className="nav-group-label">{group.label}</span>
          <ChevronDown size={16} aria-hidden="true" className="nav-group-chevron" />
        </button>
        <ul
          id={listId}
          className="nav-group-items"
          aria-labelledby={headingId}
          hidden={!open}
        >
          {group.items.map(renderItem)}
        </ul>
      </li>
    );
  };

  return (
    <>
      {/* Mobile hamburger button */}
      <button
        ref={menuButtonRef}
        className="mobile-menu-btn"
        onClick={() => setIsMobileOpen(true)}
        aria-label="Open navigation menu"
        aria-expanded={isMobileOpen}
      >
        <Menu size={22} aria-hidden="true" />
      </button>

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="sidebar-overlay"
          onClick={closeMobileSidebar}
          aria-hidden="true"
        />
      )}

      <aside
        className={`sidebar${railIsSlim ? ' is-slim' : ''}${isMobileOpen ? ' sidebar-open' : ''}`}
        aria-label="Main navigation"
        onMouseOver={showSlimTip}
        onFocus={showSlimTip}
        onMouseLeave={hideSlimTip}
        onBlur={hideSlimTip}
      >
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <div className="logo-icon">
              <Shield size={20} />
            </div>
            <div className="logo-text">
              <span className="app-name">Veriform</span>
              <span className="app-subtitle">Management System</span>
            </div>
          </div>
          <button
            ref={closeButtonRef}
            className="mobile-close-btn"
            onClick={closeMobileSidebar}
            aria-label="Close navigation menu"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        {isInitialAdmin && inTenant && (
          <div className="acting-in-strip">
            <span className="acting-in-label">Acting in</span>
            <span className="acting-in-agency">
              {/* agencyId is an opaque UUID — never paint a raw uuid at a human.
                  Neutral copy also covers a session predating P8-01, which has
                  no agencyName at all. */}
              {user.agencyName || 'Selected agency'}
            </span>
            <button
              type="button"
              className="acting-in-switch"
              onClick={handleSwitchAgency}
              aria-label="Switch agency"
              data-label="Switch agency"
            >
              <ArrowLeftRight size={14} aria-hidden="true" />
              <span>Switch agency</span>
            </button>
            {switchError && (
              <span className="acting-in-error" role="alert">
                {switchError}
              </span>
            )}
          </div>
        )}

        <nav className="sidebar-content" aria-label="Primary" onScroll={hideSlimTip}>
          <ul className="nav-list">
            {top.map(renderItem)}
          </ul>
          <ul className="nav-list nav-groups">
            {groups.map(renderGroup)}
          </ul>
        </nav>

        <div className="sidebar-utility">
          <ul className="nav-list">
            {renderItem({ to: '/settings', label: 'Settings', icon: Settings, match: exact('/settings') })}
          </ul>
          <button
            ref={collapseToggleRef}
            type="button"
            className="nav-collapse-toggle"
            onClick={toggleSlim}
            aria-pressed={railIsSlim}
            data-label={railIsSlim ? 'Expand menu' : 'Collapse menu'}
          >
            {railIsSlim
              ? <PanelLeftOpen size={18} aria-hidden="true" />
              : <PanelLeftClose size={18} aria-hidden="true" />}
            <span className="nav-item-label">{railIsSlim ? 'Expand menu' : 'Collapse menu'}</span>
          </button>
        </div>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-user-avatar" title={user?.username || undefined}>
              {getUserInitials()}
            </div>
            <div className="sidebar-user-details">
              <p className="sidebar-user-name">{user?.username || 'User'}</p>
              <p className="sidebar-user-role">{roleLabel(user?.role)}</p>
              {/* Which agency's data is on screen — exactly one indicator per
                  context. An in-tenant admin already has the "Acting in" strip,
                  so repeating the name here would just be noise. Rendered only
                  when known: a session minted before P8-01 has no agencyName, and
                  no line beats "undefined". */}
              {inMasterContext ? (
                <p className="sidebar-user-context">Master</p>
              ) : (
                !isInitialAdmin && user?.agencyName && <p className="sidebar-user-context">{user.agencyName}</p>
              )}
            </div>
            {!showLogoutConfirm ? (
              <button
                className="logout-icon"
                onClick={() => setShowLogoutConfirm(true)}
                aria-label="Logout"
              >
                <LogOut size={20} />
              </button>
            ) : (
              <div className="logout-confirm">
                <button className="btn btn-success btn-icon btn-sm btn-on-dark logout-confirm-yes" onClick={handleLogout} aria-label="Confirm logout">
                  <Check size={18} />
                </button>
                <button className="btn btn-danger btn-icon btn-sm btn-on-dark logout-confirm-no" onClick={() => setShowLogoutConfirm(false)} aria-label="Cancel logout">
                  <X size={18} />
                </button>
              </div>
            )}
          </div>
        </div>
        {slimShown && slimTip && (
          <div className="nav-slim-tip" style={{ top: slimTip.top }} aria-hidden="true">
            {slimTip.label}
          </div>
        )}
      </aside>
    </>
  );
}

export default Sidebar;
